import { useRef, useState } from "react"
import { Column, columnWrapper, customScrollColumn, plusButton, stickyHeader } from "./styled-components/column"
import { Skeleton, Tag } from "antd"
import TaskCard from "./task-card"
import { TApiProjectSection } from "@timenotes/shared/src/services/api-client/types"
import { useLoadProjectSectionTasks, useProjectBoard } from "@timenotes/shared/src/features/project-board"
import {SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import ProjectSectionHeaderContainer from "./project-section-header-container"
import NewTaskFormContainer from "./new-task-form"
import useCreateTaskMutation from "@timenotes/shared/src/services/tasks/queries/use-create-task-mutation"
import { AddButton } from "./add-button"
import { ArchivedLabel } from "./archived-label"

interface SectionColumnProps {
  projectSection: TApiProjectSection
}

const SectionColumn = (props: SectionColumnProps) => {
  const {
    projectSection,
  } = props

  const {
    isDraggingSection,
  } = useProjectBoard()

  // Disable tasks sorting on section dragging to avoid
  // drag and drop collision detection issues between 
  // sorting contexts
  const tasksSortingDisabled = !!isDraggingSection

  // CONTAINER PART

  const setNodeRef = useRef(null)
  const stickyHeaderRef = useRef(null)
  const stickyHeaderStyle = {}
  const columnWrapperStyle = columnWrapper

  const [grabbed, setGrabbed] = useState(false)
  const [sticky, setSticky] = useState(false)

  const attributes = {}
  const listeners = {}

  const {
    newTaskFormVisible,
    newTaskFormik,
    hideNewTaskForm,
    showNewTaskForm,
  } = useProjectBoard()

  // Reuse harder?
  const addTaskOnTopVisible = newTaskFormVisible && newTaskFormik.values.projectSectionId == projectSection.id && newTaskFormik.values.projectSectionPosition == 0
  const addTaskBelowVisible = newTaskFormVisible && newTaskFormik.values.projectSectionId == projectSection.id && newTaskFormik.values.projectSectionPosition != 0

  const lastTaskPosition = projectSection.tasks.tasks.length > 0 ? (projectSection.tasks.tasks[projectSection.tasks.tasks.length - 1].projectSectionPosition + 1) : 0

  const handleAddTaskAtTheEnd = () => {
    showNewTaskForm({
      projectSectionId: projectSection.id,
      projectId: projectSection.projectId,
      // New task should have the projectSectionPosition of the last loaded task increased by 1
      projectSectionPosition: lastTaskPosition
    })
  }

  const style = {
    //transform: CSS.Transform.toString(transform),
    zIndex: grabbed ? 1000 : 100,
  }

  const tasksCount = projectSection.tasks.meta.pagination.totalCount
  const canLoadMore = tasksCount > projectSection.tasks.tasks.length

  const loadMoreMutation = useLoadProjectSectionTasks({
    projectId: projectSection.projectId,
    projectSectionId: projectSection.id,
  })

  return (
    <div id={"id"}  style={{ ...style }}>
      <Column
        ref={setNodeRef}
        id={projectSection.id}
        className="column"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setGrabbed(false)
          }
        }}
        style={{ ...columnWrapperStyle, zIndex: grabbed ? 1000 : 100 }}
      >
        <div
          {...attributes}
          {...listeners}
          onMouseDown={() => setGrabbed(true)}
          onMouseUp={() => setGrabbed(false)}
          ref={stickyHeaderRef}
          className="sticky-header"
          style={{
            ...stickyHeaderStyle,
            outline: "none",
            borderBottom: sticky ? "solid 1px #ececec" : "none",
          }}
        >

          <ProjectSectionHeaderContainer 
            projectSection={projectSection} 
            style={{
              paddingLeft: '10px'
            }}
          />

          <div
            onScroll={(e: React.UIEvent<HTMLDivElement>) => {
              const target = e.target as HTMLDivElement

              setSticky(target.scrollTop > 0)

              const scrollHeight = target.scrollHeight
              const scrollTop = target.scrollTop
              const clientHeight = target.clientHeight
              const distanceFromBottom = scrollHeight - scrollTop - clientHeight
              const threshold = clientHeight * 0.2

              if (
                distanceFromBottom <= threshold &&
                !loadMoreMutation.isLoading &&
                canLoadMore
              ) {
                loadMoreMutation.mutate({})
              }
            }}
            className="custom-scroll-columm"
            style={{
              ...customScrollColumn,
              // overflowY: assigneeOpened ? "hidden" : "auto", # potentially needed fix to disable scroll?
              overflowY: 'auto',
              paddingBottom: '100px',
            }}
          >
            <ArchivedLabel 
              isArchived={projectSection.status == 'archived'} 
              style={{
                marginBottom: '10px',
              }}
            />

            <SortableContext 
              disabled={tasksSortingDisabled}
              key={projectSection.id}
              items={projectSection.tasks.tasks.map((t) => t.id)} 
              strategy={verticalListSortingStrategy}
            >
              <>
                { addTaskOnTopVisible && (
                  <NewTaskFormContainer 
                    formik={newTaskFormik}
                    onHide={hideNewTaskForm}
                  />
                )}

                {
                  projectSection.tasks.tasks.map((task) => (
                    <TaskCard 
                      key={task.id}
                      task={task}
                    />
                  ))
                }
              </>
            </SortableContext>

            { loadMoreMutation.isLoading && (
              <Skeleton active></Skeleton>
            )}

            { !loadMoreMutation.isLoading && !addTaskBelowVisible && !addTaskOnTopVisible && (
              <AddButton
                type="task"
                onClick={handleAddTaskAtTheEnd}
              >
                Add Task
              </AddButton>
            )}

            { !loadMoreMutation.isLoading && addTaskBelowVisible && (
              <NewTaskFormContainer
                formik={newTaskFormik}
                onHide={hideNewTaskForm}
              />
            )}

          </div>
        </div>
      </Column>
    </div>
  )
}

export default SectionColumn